import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { Button } from "./ui/button";
import { navLinks, companyInfo } from "../data/mock";

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileOpen(false);
  }, [location]);

  const handleNavClick = (e, href) => {
    e.preventDefault();
    setIsMobileOpen(false);

    if (href.startsWith("/#")) {
      const sectionId = href.replace("/#", "");
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => {
          const el = document.getElementById(sectionId);
          if (el) el.scrollIntoView({ behavior: "smooth" });
        }, 100);
      } else {
        const el = document.getElementById(sectionId);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate(href);
    }
  };

  const handleCallbackClick = () => {
    setIsMobileOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        const el = document.getElementById("callback");
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } else {
      const el = document.getElementById("callback");
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-[#0a2a43]/95 backdrop-blur-md shadow-lg shadow-[#0a2a43]/10"
          : "bg-[#0a2a43]"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2 group"
            onClick={(e) => handleNavClick(e, "/#home")}
          >
            <div className="w-9 h-9 rounded-lg bg-[#ff9900] flex items-center justify-center">
              <span className="text-white font-bold text-lg">A</span>
            </div>
            <span className="text-white font-semibold text-lg tracking-tight group-hover:text-[#ff9900] transition-colors duration-200">
              {companyInfo.name}
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className="px-4 py-2 text-sm text-white/80 hover:text-[#ff9900] transition-colors duration-200 rounded-lg hover:bg-white/5 font-medium"
              >
                {link.label}
              </a>
            ))}
            <Button
              onClick={handleCallbackClick}
              className="ml-3 bg-[#ff9900] hover:bg-[#e68a00] text-white font-semibold px-5 py-2.5 rounded-lg shadow-md shadow-[#ff9900]/20 hover:shadow-lg hover:shadow-[#ff9900]/30 transition-all duration-200"
            >
              <Phone className="w-4 h-4 mr-2" />
              Request Callback
            </Button>
          </div>

          {/* Mobile Toggle */}
          <button
            onClick={() => setIsMobileOpen(!isMobileOpen)}
            className="lg:hidden text-white p-2 rounded-lg hover:bg-white/10 transition-colors duration-200"
            aria-label="Toggle menu"
          >
            {isMobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-300 ease-in-out ${
          isMobileOpen ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="px-4 pb-6 pt-2 bg-[#0a2a43] border-t border-white/10">
          {navLinks.map((link) => (
            <a
              key={link.label}
              href={link.href}
              onClick={(e) => handleNavClick(e, link.href)}
              className="block px-4 py-3 text-white/80 hover:text-[#ff9900] hover:bg-white/5 rounded-lg transition-colors duration-200 font-medium"
            >
              {link.label}
            </a>
          ))}
          <Button
            onClick={handleCallbackClick}
            className="w-full mt-3 bg-[#ff9900] hover:bg-[#e68a00] text-white font-semibold py-3 rounded-lg"
          >
            <Phone className="w-4 h-4 mr-2" />
            Request Callback
          </Button>
        </div>
      </div>
    </nav>
  );
};
